import { formatLocalDateTime } from '../utils/localDateTime';
import { confirmApp } from '../common/appDialog';
import React, { useEffect, useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import type { Organization } from '@types';
import { subscriptionStatusLabel } from '../utils/subscriptionStatusLabel';

interface SubscriptionComponent {
    priceId: string;
    productId: string;
    productName: string;
    priceName: string;
    productType: string;
    status: string;
    quantity: number;
    unitAmount: string;
    subtotalAmount: string;
    currencyCode: string;
    productMetadata: Record<string, unknown>;
    priceMetadata: Record<string, unknown>;
}

interface SubscriptionOverview {
    id: string;
    customerId: string;
    status: string;
    hasMsaSubscription: boolean;
    currencyCode: string;
    currentAmount: string;
    billingCycle?: 'monthly' | 'annual';
    startedAt: string;
    currentPeriodStartsAt: string;
    currentPeriodEndsAt: string;
    nextBilledAt: string;
    pausedAt: string;
    canceledAt: string;
    scheduledAction: string;
    scheduledEffectiveAt: string;
    components: SubscriptionComponent[];
}

function label(value: string): string {
    return value.replace(/^msa-/, '').replace(/[_-]+/g, ' ').replace(/\b\w/g, character => character.toUpperCase());
}

function date(value?: string): string {
    if (!value)
        return '—';
    return formatLocalDateTime(value, value);
}

function money(amount: string, currency: string): string {
    const value = Number(amount);
    if (!Number.isFinite(value))
        return '—';
    try {
        return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(value / 100);
    } catch {
        return `${(value / 100).toFixed(2)} ${currency || 'USD'}`;
    }
}

function metadata(component: SubscriptionComponent): [string, string][] {
    const combined = { ...component.productMetadata, ...component.priceMetadata };
    return Object.entries(combined)
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim())
        .map(([key, value]) => [label(key), Array.isArray(value) ? value.join(', ') : String(value)]);
}

const SummaryItem: React.FC<{ title: string; value: string }> = ({ title, value }) => (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{title}</p>
        <p className="mt-1 text-sm font-extrabold text-slate-900">{value}</p>
    </div>
);

const OrganizationSubscriptionStatusPanel: React.FC<{ organization: Organization; canCreateTrialLink?: boolean; canResetMsaTrial?: boolean; onReset?: () => Promise<void> | void }> = ({ organization, canCreateTrialLink = false, canResetMsaTrial = false, onReset }) => {
    const [subscription, setSubscription] = useState<SubscriptionOverview | null>(null);
    const [loading, setLoading] = useState(true);
    const [loaded, setLoaded] = useState(false);
    const [launching, setLaunching] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [error, setError] = useState('');

    const launchMsa = async () => {
        setLaunching(true);
        setError('');
        const target = window.open('', '_blank');
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'createOrganizationMsaLink');
            const response = await callable({ organizationId: organization.id });
            const url = String((response.data as { url?: string }).url || '');
            if (!url)
                throw new Error('The MSA test link was not created.');
            if (target) {
                target.opener = null;
                target.location.href = url;
            } else
                window.location.href = url;
        } catch (failure: any) {
            target?.close();
            setError(failure?.message || 'The free trial could not start.');
        } finally {
            setLaunching(false);
        }
    };

    const resetMsa = async () => {
        if (!await confirmApp('Reset this free trial and any signed MSA? Any trialing Paddle subscription will be canceled. Organization and user details will remain.', { title: 'Reset Free Trial', confirmLabel: 'Reset Trial', tone: 'danger' }))
            return;
        setResetting(true);
        setError('');
        try {
            const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'resetOrganizationMsaTest');
            await callable({ organizationId: organization.id });
            await onReset?.();
        } catch (failure: any) {
            setError(failure?.message || 'The free trial could not reset.');
        } finally {
            setResetting(false);
        }
    };

    const resetButton = canResetMsaTrial && organization.msaApplicationId && (organization.msaTrialMode === 'app' || ['trialing', 'on_trial'].includes(organization.subscriptionStatus || '')) && organization.subscriptionStatus !== 'active' ? <button type="button" disabled={resetting} onClick={resetMsa} className="rounded-xl border border-red-200 px-5 py-3 text-sm font-extrabold text-red-700 hover:bg-red-50 disabled:opacity-50">{resetting ? 'Resetting trial…' : 'Reset Free Trial'}</button> : null;

    useEffect(() => {
        let active = true;
        setLoading(true);
        setLoaded(false);
        setError('');
        if (organization.isTestOrganization) {
            setSubscription(null);
            setLoaded(true);
            setLoading(false);
            return;
        }
        const callable = httpsCallable(getFunctions(undefined, 'us-central1'), 'manageOrganizationBilling');
        void callable({ action: 'getSubscriptionOverview', data: { organizationId: organization.id } })
            .then(response => {
                if (active) {
                    setSubscription((response.data as { subscription?: SubscriptionOverview | null }).subscription || null);
                    setLoaded(true);
                }
            })
            .catch(failure => {
                if (active)
                    setError(failure?.message || 'Subscription status failed loading.');
            })
            .finally(() => {
                if (active)
                    setLoading(false);
            });
        return () => {
            active = false;
        };
    }, [organization.id, organization.isTestOrganization]);

    if (organization.isTestOrganization)
        return <section className="rounded-2xl border border-amber-200 bg-amber-50 p-6 shadow-sm"><h3 className="text-xl font-extrabold text-amber-950">Test Organization</h3><p className="mt-2 text-sm text-amber-900">Subscription access is enabled until a Super administrator turns off test mode. Service requests can be billed or completed for free.</p></section>;

    if (loading)
        return <section className="rounded-2xl border border-slate-200 bg-white p-12 text-center text-sm font-semibold text-slate-400 shadow-sm">Loading subscription...</section>;

    if (error && !loaded)
        return <section className="rounded-2xl border border-red-200 bg-red-50 p-6 text-sm font-semibold text-red-700 shadow-sm">{error}</section>;

    const pricePeriod = (subscription?.billingCycle || organization.msaPricing?.billingCycle || organization.billingCycle) === 'monthly' ? 'Monthly' : 'Annual';

    if (!subscription && organization.msaTrialMode === 'app' && organization.trialEndsAt)
        return <section className="space-y-5 rounded-2xl border border-blue-200 bg-white p-6 shadow-sm"><div className="flex items-center justify-between gap-3"><div><h3 className="text-xl font-extrabold text-slate-900">Free trial</h3><p className="mt-1 text-sm text-slate-500">Free trial access; payment has not been set up.</p></div><span className="rounded-full bg-blue-100 px-3 py-1 text-xs font-extrabold uppercase text-blue-700">{new Date(organization.trialEndsAt).getTime() > Date.now() ? 'Trial Active' : 'Trial Expired'}</span></div><div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4"><SummaryItem title="MSA Reference" value={organization.msaReference || 'Not recorded'} /><SummaryItem title="Trial Started" value={date(organization.trialStartedAt || organization.msaEffectiveAt)} /><SummaryItem title="Trial Ends" value={date(organization.trialEndsAt)} />{organization.msaPricing && <SummaryItem title={`${pricePeriod} Price`} value={money(String(organization.msaPricing.total || 0), organization.msaPricing.currencyCode || 'USD')} />}</div><p className="text-sm text-slate-600">Payment status: {organization.msaPaymentStatus === 'canceled' ? 'Canceled before billing' : 'Not set up'}</p>{resetButton && <div className="flex justify-end">{resetButton}</div>}</section>;

    if (!subscription || !subscription.hasMsaSubscription) {
        const missing = !organization.hasMsaSubscription && !organization.msaApplicationId && !organization.msaDocument;
        return <section className="rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm"><h3 className="text-xl font-extrabold text-slate-900">{missing ? 'No Enterprise Subscription' : 'Enterprise Subscription Inactive'}</h3><p className="mt-2 text-sm text-slate-500">{missing ? 'This organization has no linked Enterprise subscription.' : 'This organization has prior MSA history without an active subscription.'}</p>{organization.msaReference && <p className="mt-3 text-sm font-extrabold text-blue-700">MSA Reference: {organization.msaReference}</p>}<span className="mt-4 inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-bold uppercase text-slate-600">Inactive</span><div className="mt-6 flex justify-center gap-3">{missing && canCreateTrialLink && <button type="button" disabled={launching} onClick={launchMsa} className="rounded-xl bg-blue-600 px-5 py-3 text-sm font-extrabold text-white hover:bg-blue-700 disabled:opacity-50">{launching ? 'Preparing free trial…' : 'Begin Free Trial'}</button>}{resetButton}</div>{error && <p className="mt-4 rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</p>}</section>;
    }

    const active = subscription.hasMsaSubscription && ['active', 'trialing', 'on_trial'].includes(subscription.status);
    const renewalLabel = subscription.scheduledAction === 'cancel' ? 'Access Ends' : 'Renews';

    return <section className="space-y-5 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div><h3 className="text-xl font-extrabold text-slate-900">Enterprise Subscription</h3><p className="mt-1 text-sm text-slate-500">Current subscription details.</p></div>
            <span className={`rounded-full px-3 py-1 text-xs font-extrabold uppercase ${active ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{subscriptionStatusLabel(subscription.status, organization.isTestOrganization, 'Inactive')}</span>
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <SummaryItem title="MSA Reference" value={organization.msaReference || 'Not recorded'} />
            <SummaryItem title={`Current ${pricePeriod} Price`} value={money(subscription.currentAmount, subscription.currencyCode)} />
            <SummaryItem title="Started" value={date(subscription.startedAt)} />
            <SummaryItem title={renewalLabel} value={date(subscription.scheduledEffectiveAt || subscription.nextBilledAt || subscription.currentPeriodEndsAt)} />
            <SummaryItem title="Industry" value={organization.industry || 'Not recorded'} />
        </div>

        {subscription.scheduledAction && <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm font-semibold text-amber-800">Scheduled {label(subscription.scheduledAction)}: {date(subscription.scheduledEffectiveAt)}</div>}

        <div>
            <h4 className="mb-3 font-extrabold text-slate-900">Pricing Components</h4>
            <div className="space-y-3">
                {subscription.components.map(component => (
                    <article key={component.priceId} className="rounded-xl border border-slate-200 p-4">
                        <div className="flex flex-wrap items-start justify-between gap-3">
                            <div><p className="font-bold text-slate-900">{component.productName}</p><p className="text-xs text-slate-500">{component.priceName || label(component.productType)}</p></div>
                            <div className="text-right"><p className="font-extrabold text-slate-900">{money(component.subtotalAmount, component.currencyCode)}</p><p className="text-xs text-slate-500">{component.quantity} × {money(component.unitAmount, component.currencyCode)}</p></div>
                        </div>
                        {!!metadata(component).length && <dl className="mt-4 grid gap-2 border-t border-slate-100 pt-3 sm:grid-cols-2 xl:grid-cols-3">{metadata(component).map(([key, value]) => <div key={`${component.priceId}-${key}`}><dt className="text-[10px] font-bold uppercase tracking-wide text-slate-400">{key}</dt><dd className="text-sm font-semibold text-slate-700">{value}</dd></div>)}</dl>}
                        <p className="mt-3 break-all font-mono text-[10px] text-slate-400">{component.priceId}</p>
                    </article>
                ))}
                {!subscription.components.length && <p className="rounded-xl border border-dashed border-slate-300 p-8 text-center text-sm text-slate-400">No recurring pricing components found.</p>}
            </div>
        </div>

        <div className="grid gap-3 border-t border-slate-200 pt-4 text-xs text-slate-500 sm:grid-cols-2">
            <p><span className="font-bold text-slate-700">Subscription:</span> {subscription.id}</p>
            <p><span className="font-bold text-slate-700">Customer:</span> {subscription.customerId || '—'}</p>
            <p><span className="font-bold text-slate-700">Current period:</span> {date(subscription.currentPeriodStartsAt)}</p>
            <p><span className="font-bold text-slate-700">Period end:</span> {date(subscription.currentPeriodEndsAt)}</p>
        </div>
        {resetButton && <div className="flex justify-end border-t border-slate-200 pt-5">{resetButton}</div>}
        {error && <p className="rounded-xl bg-red-50 p-3 text-sm font-semibold text-red-700">{error}</p>}
    </section>;
};

export default OrganizationSubscriptionStatusPanel;
